import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  createContactEvent,
  createMember,
  createRelationship,
  fetchAuditLog,
  fetchChild,
  fetchChildAnalytics,
  fetchContactEvents,
  fetchNetwork,
  fetchSearchSources,
  fetchSupervisionAnalytics,
  mergeMembers,
  runSearch,
  updateMember
} from '../api/mockApi';
import { computeNetworkHealth, categorizeActivity } from '../utils/networkHealth';

const FamilyFinderContext = createContext(null);

const defaultActor = 'caseworker.jordan@safegenerations.org';

export const FamilyFinderProvider = ({ childId, children }) => {
  const [child, setChild] = useState(null);
  const [members, setMembers] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [contactEvents, setContactEvents] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchSources, setSearchSources] = useState([]);
  const [auditEntries, setAuditEntries] = useState([]);
  const [supervisionAnalytics, setSupervisionAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadChildData = useCallback(async () => {
    if (!childId) return;
    try {
      setLoading(true);
      const [childProfile, network, contacts, audit] = await Promise.all([
        fetchChild(childId),
        fetchNetwork(childId),
        fetchContactEvents({ childId }),
        fetchAuditLog({})
      ]);
      setChild(childProfile);
      setMembers(network.members);
      setRelationships(network.relationships);
      setContactEvents(contacts);
      setAuditEntries(audit);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadChildData();
  }, [loadChildData]);

  useEffect(() => {
    const loadSources = async () => {
      const sources = await fetchSearchSources();
      setSearchSources(sources);
    };
    loadSources();
  }, []);

  const refreshSupervisionAnalytics = useCallback(async () => {
    const analytics = await fetchSupervisionAnalytics();
    setSupervisionAnalytics(analytics);
  }, []);

  useEffect(() => {
    refreshSupervisionAnalytics();
  }, [refreshSupervisionAnalytics]);

  const health = useMemo(() => {
    if (!child)
      return {
        score: 0,
        summary: {
          activeMembers: 0,
          activeScore: 0,
          roleScore: 0,
          conflictPenalty: 0,
          conflicts: 0,
          totalMembers: 0
        }
      };
    return computeNetworkHealth({ members, relationships });
  }, [child, members, relationships]);

  const logAudit = useCallback((entry) => {
    setAuditEntries((prev) => [entry, ...prev]);
  }, []);

  const handleMemberCreated = useCallback((member, auditEntry) => {
    setMembers((prev) => [...prev, member]);
    logAudit(auditEntry);
    refreshSupervisionAnalytics();
  }, [logAudit, refreshSupervisionAnalytics]);

  const handleMemberUpdated = useCallback((member, auditEntry) => {
    setMembers((prev) => prev.map((existing) => (existing._id === member._id ? member : existing)));
    logAudit(auditEntry);
    refreshSupervisionAnalytics();
  }, [logAudit, refreshSupervisionAnalytics]);

  const handleMemberMerged = useCallback((targetMember, auditEntry, sourceId) => {
    setMembers((prev) =>
      prev
        .filter((member) => member._id !== sourceId)
        .map((member) => (member._id === targetMember._id ? targetMember : member))
    );
    logAudit(auditEntry);
    refreshSupervisionAnalytics();
  }, [logAudit, refreshSupervisionAnalytics]);

  const handleRelationshipCreated = useCallback((relationship, auditEntry) => {
    setRelationships((prev) => [...prev, relationship]);
    logAudit(auditEntry);
  }, [logAudit]);

  const handleContactLogged = useCallback((event, auditEntry) => {
    setContactEvents((prev) => [event, ...prev]);
    logAudit(auditEntry);
    refreshSupervisionAnalytics();
  }, [logAudit, refreshSupervisionAnalytics]);

  const addMember = useCallback(
    async (payload, actor = defaultActor) => {
      if (!childId) throw new Error('childId required to add member');
      const { member, auditEntry } = await createMember({ childId, payload, actor });
      handleMemberCreated(member, auditEntry);
      return member;
    },
    [childId, handleMemberCreated]
  );

  const updateMemberProfile = useCallback(
    async (memberId, updates, actor = defaultActor) => {
      const { member, auditEntry } = await updateMember({ memberId, updates, actor });
      handleMemberUpdated(member, auditEntry);
      return member;
    },
    [handleMemberUpdated]
  );

  const mergeMemberRecords = useCallback(
    async (sourceId, targetId, actor = defaultActor) => {
      const { member, auditEntry } = await mergeMembers({ sourceId, targetId, actor });
      handleMemberMerged(member, auditEntry, sourceId);
      return member;
    },
    [handleMemberMerged]
  );

  const addRelationship = useCallback(
    async (payload, actor = defaultActor) => {
      const { relationship, auditEntry } = await createRelationship({ payload, actor });
      handleRelationshipCreated(relationship, auditEntry);
      return relationship;
    },
    [handleRelationshipCreated]
  );

  const logContactEvent = useCallback(
    async (payload, actor = defaultActor) => {
      const { event, auditEntry, updatedMember } = await createContactEvent({ payload, actor });
      handleContactLogged(event, auditEntry);
      if (updatedMember) {
        setMembers((prev) =>
          prev.map((member) => (member._id === updatedMember._id ? updatedMember : member))
        );
      }
      return event;
    },
    [handleContactLogged]
  );

  const executeSearch = useCallback(
    async (query) => {
      if (!childId) return [];
      const results = await runSearch({ query, childId });
      setSearchResults(results);
      return results;
    },
    [childId]
  );

  const loadAnalytics = useCallback(async () => {
    if (!childId) return null;
    return fetchChildAnalytics(childId);
  }, [childId]);

  const activityByMember = useMemo(() => {
    const mapping = new Map();
    members.forEach((member) => {
      mapping.set(member._id, categorizeActivity(member));
    });
    return mapping;
  }, [members]);

  const value = useMemo(
    () => ({
      child,
      childId,
      members,
      relationships,
      contactEvents,
      searchResults,
      searchSources,
      auditEntries,
      supervisionAnalytics,
      loading,
      error,
      health,
      addMember,
      updateMemberProfile,
      mergeMemberRecords,
      addRelationship,
      logContactEvent,
      executeSearch,
      loadAnalytics,
      refresh: loadChildData,
      refreshSupervisionAnalytics,
      activityByMember
    }),
    [
      child,
      childId,
      members,
      relationships,
      contactEvents,
      searchResults,
      searchSources,
      auditEntries,
      supervisionAnalytics,
      loading,
      error,
      health,
      addMember,
      updateMemberProfile,
      mergeMemberRecords,
      addRelationship,
      logContactEvent,
      executeSearch,
      loadAnalytics,
      loadChildData,
      refreshSupervisionAnalytics,
      activityByMember
    ]
  );

  return <FamilyFinderContext.Provider value={value}>{children}</FamilyFinderContext.Provider>;
};

export const useFamilyFinder = () => {
  const context = useContext(FamilyFinderContext);
  if (!context) {
    throw new Error('useFamilyFinder must be used within a FamilyFinderProvider');
  }
  return context;
};
